import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api-client';
import { useAuth } from './AuthContext';

const SESSION_KEY = 'activeCompanyId';

interface CompanyInfo {
  id: string;
  name: string;
  code: string;
  status: string;
}

interface CompanyContextType {
  activeCompanyId: string | null;
  activeCompany: CompanyInfo | null;
  companies: CompanyInfo[];
  setActiveCompany: (id: string) => void;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | null>(null);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(() => {
    if (!isAdmin && user?.companyId) return user.companyId;
    return sessionStorage.getItem(SESSION_KEY);
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => apiGet<{ items: CompanyInfo[] }>('/admin/companies'),
    enabled: isAdmin,
  });

  const companies: CompanyInfo[] = isAdmin && data?.ok ? data.data.items : [];

  const nonAdminCompany: CompanyInfo | null =
    !isAdmin && user?.companyId
      ? { id: user.companyId, name: '', code: '', status: 'active' }
      : null;

  const activeCompany =
    companies.find((c) => c.id === activeCompanyId) ?? (!isAdmin ? nonAdminCompany : null);

  const setActiveCompany = useCallback((id: string) => {
    setActiveCompanyIdState(id);
    sessionStorage.setItem(SESSION_KEY, id);
  }, []);

  useEffect(() => {
    if (isAdmin && companies.length > 0 && !activeCompanyId) {
      const first = companies.find((c) => c.status === 'active') ?? companies[0];
      setActiveCompany(first.id);
    }
  }, [isAdmin, companies, activeCompanyId, setActiveCompany]);

  return (
    <CompanyContext.Provider
      value={{ activeCompanyId, activeCompany, companies, setActiveCompany, isLoading }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyContextType {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within a CompanyProvider');
  return ctx;
}
